import { fas } from "@fortawesome/free-solid-svg-icons";

/**
 * Custom hook to handle message attachments.
 * @returns {{
 *   checkAttachments: (function(Object, *[], *[]=): *[]),
 *   processAttachmentIds: (function(string[], *[]): *[]),
 *   fileConfig: {acceptedFileTypes: string, acceptedFileSize: number, byteConversionFactor: number, sizeUnit: string, acceptedFileAmount: number}
 * }}
 */
const useMessageAttachments = () => {
    /**
     * Check attachments, combine them to comments and sort by date to show them in the correct order.
     *
     * @param {object} message
     * @param {*[]} attachments
     * @param {*[]} comments
     * @returns {*[]} combined array of comments and attachments
     */
    const checkAttachments = (message, attachments, comments = []) => {
        let allAttachments = [];
        const isSupport = !!message.is_support;

        // Extract all attachment IDs from comments
        const commentAttachmentIds = comments.reduce((ids, comment) => {
            if (comment.attachment_ids && comment.comment.length > 0) {
                ids.push(...comment.attachment_ids);
            }
            return ids;
        }, []);

        attachments.forEach((attachment) => {
            // Check if the attachment ID is found in the comments
            if (!commentAttachmentIds.includes(attachment.id)) {
                // Add fields for easier handling on the UI
                attachment.sent_date = attachment.created;
                attachment.type = 'attachment';
                attachment.attachment_ids = [attachment.id];
                attachment.is_support = isSupport;

                allAttachments.push(attachment);
            }
        });

        // combine comments and attachments for sorting
        let combinedArray = comments.concat(allAttachments);

        // sort attachments by date
        combinedArray.sort((a, b) => {
            return new Date(a.sent_date) - new Date(b.sent_date);
        });

        // return combined array of comments and attachments
        return combinedArray;
    }

    /**
     * Process attachment ids and return buttons for each attachment.
     * @param {string[]} attachmentIds
     * @param {*[]} attachments
     * @returns {*[]}
     */
    const processAttachmentIds = (attachmentIds, attachments) => {
        // Define a mapping of file extensions to FontAwesome icons
        const fileTypeIcons = {
            'jpg': fas.faFileImage,
            'jpeg': fas.faFileImage,
            'png': fas.faFileImage,
            'svg': fas.faFileImage,
            'tif': fas.faFileImage,
            'tiff': fas.faFileImage,
            'gif': fas.faFileImage,
            'bmp': fas.faFileImage,
            'docx': fas.faFileWord,
            'doc': fas.faFileWord,
            'odt': fas.faFileWord,
            'xlsx': fas.faFileExcel,
            'xls': fas.faFileExcel,
            'ods': fas.faFileExcel,
            'pptx': fas.faFilePowerpoint,
            'ppt': fas.faFilePowerpoint,
            'odp': fas.faFilePowerpoint,
            'pdf': fas.faFilePdf,
            'txt': fas.faFileAlt,
            'csv': fas.faFileCsv
        };

        // Set to keep track of filenames that have already been processed
        const processedFilenames = new Set();

        // filter out attachments
        const matchedAttachments = attachmentIds.map(attachmentId => {
            const attachment = attachments.find(att => att.id === attachmentId);
            return attachment ? { id: attachmentId, filename: attachment.filename, mimetype: attachment.mimetype } : null;
        }).filter(att => att !== null);

        return matchedAttachments.map((attachment) => {
            if (attachment.filename && !processedFilenames.has(attachment.filename)) {
                // Add filename to the set of processed filenames
                processedFilenames.add(attachment.filename);

                // Extract file extension
                const fileExtension = attachment.filename.split('.').pop().toLowerCase();

                // Determine the FontAwesome icon
                const icon = fileTypeIcons.hasOwnProperty(fileExtension) ? fileTypeIcons[fileExtension] : fas.faFile;

                return { icon, attachment };
            }
        }).filter(att => att !== null && att !== undefined);
    }

    const fileConfig = {
        acceptedFileTypes: '.jpg,.jpeg,.png,.svg,.tif,.tiff,.gif,.bmp,.docx,.doc,.odt,.xlsx,.xls,.ods,.pptx,.ppt,.odp,.pdf,.txt,.csv',
        acceptedFileSize: 30,
        byteConversionFactor: 1024 * 1024,
        sizeUnit: 'MB',
        acceptedFileAmount: 10
    }

    return {
        checkAttachments,
        processAttachmentIds,
        fileConfig
    }
}

export default useMessageAttachments;
